import { useCallback, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LoginModeResponse } from '@asset-metrix/portal-auth';
import {
  LoginWidget,
  ResetPasswordConfirmationWidget,
  ResetPasswordWidget,
} from '@asset-metrix/portal-ui';
import { useShallow } from 'zustand/react/shallow';

import { setLoginModeData } from '../api/portalApi/loginMode';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { useStore } from '../store';
import { extractErrorMessage } from '../utils/errorMessage';
import { redirectToSsoLogin, redirectToSsoResetPwUrl } from '../utils/sso';

import styles from './login.module.scss';

export const Login = () => {
  const navigate = useNavigate();
  const { state } = useLocation();
  const [loginMode, setLoginMode] =
    useState<LoginModeResponse['mode']>('local');
  const [loginState, setLoginState] = useState<
    'login' | 'reset' | 'reset_sent'
  >('login');
  const [loginError, setLoginError] = useState<string | undefined>();
  const [resetPasswordError, setResetPasswordError] = useState<
    string | undefined
  >();

  const [initialLoginModeData, loginSession, session] = useStore(
    useShallow((state) => [
      state.initialLoginModeData,
      state.loginSession,
      state.session,
    ])
  );

  useDocumentTitle('Login');

  const checkLoginMode = useCallback(
    async (email: string) => {
      setLoginError(undefined);
      try {
        const resp = await session?.getLoginMode(email);
        if (resp?.mode) {
          setLoginMode(resp?.mode);
          // update query cache
          setLoginModeData(resp);
        }
      } catch (error: unknown) {
        setLoginError(extractErrorMessage(error, 'Login mode not available'));
      }
    },
    [session]
  );

  async function onLogin(email: string, password: string) {
    setLoginError(undefined);
    try {
      switch (loginMode) {
        case 'sso': {
          // Deep link redirect is handled within SSO
          const redirectUrl = session?.getRedirectUrl({ clear: true });

          redirectToSsoLogin({
            email,
            redirect_uri: redirectUrl,
          });
          break;
        }
        case 'local': {
          await loginSession(email, password);
          const redirectUrl = session?.getRedirectUrl({ clear: true });
          if (redirectUrl) {
            // Need to use basic browser functionality, because redirect url includes e.g. the router basename
            globalThis.location.assign(redirectUrl);
          } else {
            navigate('/');
          }
          break;
        }
        case 'multi':
          break;
        default:
          return setLoginError('Unknown login mode detected.');
      }
    } catch (error: unknown) {
      setLoginError(extractErrorMessage(error, 'Login failed'));
    }
  }

  function onResetPassword(email: string) {
    setResetPasswordError(undefined);
    if (loginMode === 'sso') {
      redirectToSsoResetPwUrl(email);
    } else {
      setLoginState('reset');
    }
  }

  async function onSubmitResetPassword(email: string) {
    if (loginMode === 'sso') {
      redirectToSsoResetPwUrl(email);
    } else {
      try {
        await session?.resetPassword(email);
        setLoginState('reset_sent');
      } catch (error: unknown) {
        setResetPasswordError(
          extractErrorMessage(error, 'Reset password failed')
        );
      }
    }
  }

  if (initialLoginModeData?.mode === 'sso') {
    // Deep link redirect is handled within SSO
    const redirectUrl = session?.getRedirectUrl({ clear: true });

    redirectToSsoLogin({
      redirect_uri: redirectUrl,
    });
    return;
  }

  function renderWidget() {
    switch (loginState) {
      case 'reset':
        return (
          <ResetPasswordWidget
            isSso={loginMode === 'sso'}
            onBackToLogin={() => setLoginState('login')}
            onEnterValidEmail={
              initialLoginModeData?.mode === 'multi'
                ? checkLoginMode
                : undefined
            }
            onSubmit={onSubmitResetPassword}
            error={resetPasswordError}
          />
        );
      case 'reset_sent':
        return (
          <ResetPasswordConfirmationWidget
            onBackToLogin={() => setLoginState('login')}
          />
        );
      default:
        return (
          <LoginWidget
            introductionText={state && state.logoutReason}
            isSso={loginMode === 'sso'}
            loginError={loginError}
            onEnterValidEmail={
              initialLoginModeData?.mode === 'multi'
                ? checkLoginMode
                : undefined
            }
            onLoginSubmit={onLogin}
            onResetPassword={onResetPassword}
          />
        );
    }
  }

  return (
    <div className={styles['login__root']}>
      <div className={styles['login__tenant-logo']} />
      {renderWidget()}
    </div>
  );
};
